import createClient from 'openapi-fetch';
import { ErroDaApi, erroDaResposta, erroSemConexao } from './erros';
import type { paths } from './schema';

export const TEMPO_ESGOTADO_MS = 30_000;

export type ClienteDaApi = ReturnType<typeof criarCliente>;

interface OpcoesDoCliente {
  /** Origem do painel; a API está no mesmo domínio (ADR-0014). */
  baseUrl?: string;
  tempoEsgotadoMs?: number;
}

export type Buscador = (pedido: Request) => Promise<Response>;

/**
 * O `fetch` de todo o painel (API e Better Auth). Cada chamada vai com um `X-Request-Id` novo e
 * tempo esgotado de 30 s; toda resposta que não é 2xx vira `ErroDaApi`, então quem chama só
 * recebe sucesso (CA-04 a CA-06 da spec 000).
 */
export function criarBuscador(tempoEsgotadoMs = TEMPO_ESGOTADO_MS): Buscador {
  return async (pedido) => {
    const requestId = crypto.randomUUID();
    const headers = new Headers(pedido.headers);
    headers.set('X-Request-Id', requestId);
    const sinal = AbortSignal.any([pedido.signal, AbortSignal.timeout(tempoEsgotadoMs)]);
    let resposta: Response;
    try {
      // `globalThis.fetch` na hora da chamada, para o MSW interceptar nos testes.
      resposta = await globalThis.fetch(
        new Request(pedido, { headers, signal: sinal, credentials: 'include' }),
      );
    } catch (erro) {
      // Cancelamento pedido por quem chamou (ex. TanStack Query desmontando) segue como está.
      if (pedido.signal.aborted) throw erro;
      throw erroSemConexao(erro, requestId);
    }
    if (!resposta.ok) throw await erroDaResposta(resposta);
    return resposta;
  };
}

/** Cliente tipado pelo OpenAPI; único ponto de HTTP da `/api/v1` (docs/02). */
export function criarCliente(opcoes: OpcoesDoCliente = {}) {
  const buscar = criarBuscador(opcoes.tempoEsgotadoMs);
  return createClient<paths>({
    baseUrl: opcoes.baseUrl ?? window.location.origin,
    credentials: 'include',
    headers: { Accept: 'application/json' },
    fetch: buscar,
  });
}

/**
 * Tira o `data` de uma chamada (as falhas já viraram `ErroDaApi`). Para respostas sem corpo
 * (204), use a chamada direto.
 */
export async function dados<T>(chamada: Promise<{ data?: T; response: Response }>): Promise<T> {
  const { data, response } = await chamada;
  if (data === undefined) {
    throw new ErroDaApi({ status: response.status, code: 'RESPOSTA_INVALIDA' });
  }
  return data;
}
